import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppAuth } from '../../hooks/useAppAuth';
import { useAuth } from './AuthProvider';
import { Loader2, Lock } from 'lucide-react';
import { authGate } from '../../lib/authGate';

interface GatedRouteProps {
    children: React.ReactNode;
    /**
     * Custom message to show in the gate modal
     */
    gateMessage?: string;
    /**
     * Custom title for the gate modal
     */
    gateTitle?: string;
}

/**
 * GatedRoute - Shows different modals based on auth status:
 * - Logged-out users: AuthGateModal (sign-in/sign-up)
 * - Logged-in users without investor account: UpgradeModal (investor account required)
 * Use this for tools/features that require an investor account
 */
export const GatedRoute: React.FC<GatedRouteProps> = ({ 
    children, 
    gateMessage = "Create a free investor account to access advanced investment tools and analysis.",
    gateTitle = "Investor Account Required"
}) => {
    const { authStatus, authLoading, userProfile } = useAppAuth();
    const { authReady, user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    // Check if user has investor account
    const hasInvestorAccount = userProfile?.buyerType === 'Investor';

    // Trigger appropriate modal based on auth status
    useEffect(() => {
        if (!authReady || authLoading) return;

        // If user is logged out, trigger sign-in modal
        if (authStatus === 'logged_out' || !user) {
            authGate.openGate('tools', {
                path: location.pathname,
                action: 'access-tools',
                targetPath: location.pathname // Will navigate here after auth
            });
        } 
        // If user is logged in but doesn't have investor account, trigger investor account modal
        else if (user && !hasInvestorAccount) {
            // Use a custom event or callback to trigger UpgradeModal
            // We'll use a custom event that App.tsx listens to
            window.dispatchEvent(new CustomEvent('openInvestorAccountModal', {
                detail: { path: location.pathname }
            }));
        }
    }, [authReady, authLoading, authStatus, user, hasInvestorAccount, location.pathname]);

    // Show loading while auth is initializing
    if (!authReady || authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <Loader2 className="size-8 animate-spin text-slate-300" />
            </div>
        );
    }

    // If user is not authenticated, show placeholder (modal is handled by App.tsx)
    if (authStatus === 'logged_out' || !user) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white p-4">
                <div className="text-center max-w-md">
                    <div className="size-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Lock size={32} className="text-slate-400" />
                    </div>
                    <h2 className="text-xl font-bold text-slate-900 mb-2">{gateTitle}</h2>
                    <p className="text-slate-500 mb-6">{gateMessage}</p>
                    <button
                        onClick={() => navigate('/marketplace')}
                        className="px-6 py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-colors"
                    >
                        Browse Marketplace
                    </button>
                </div>
            </div>
        );
    }

    // If user is logged in but doesn't have investor account, show placeholder (modal is handled by App.tsx)
    if (user && !hasInvestorAccount) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white p-4">
                <div className="text-center max-w-md">
                    <div className="size-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Lock size={32} className="text-slate-400" />
                    </div>
                    <h2 className="text-xl font-bold text-slate-900 mb-2">{gateTitle}</h2>
                    <p className="text-slate-500 mb-6">{gateMessage}</p>
                    <button
                        onClick={() => navigate('/marketplace')}
                        className="px-6 py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-colors"
                    >
                        Browse Marketplace
                    </button>
                </div>
            </div>
        );
    }

    // User is authenticated and has investor account, show the protected content
    return <>{children}</>;
};
